import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, Shield, Calendar, Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminProfileTab() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/profile");
      setProfile(res.data);
    } catch (e) {
      toast.error("Kunde inte ladda profil");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("De nya lösenorden matchar inte");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Lösenordet måste vara minst 6 tecken");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/profile/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      toast.success("Lösenord uppdaterat!");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Kunde inte ändra lösenord");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#D4B483] animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-[#D4D4D8]">
        <p>Kunde inte ladda profil</p>
        <Button onClick={loadProfile} className="mt-4 bg-[#D4B483] text-[#0A0A0A]">
          Försök igen
        </Button>
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("sv-SE", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Profile Info */}
      <div className="bg-[#121212] rounded-xl border border-[#27272A] p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 rounded-full bg-[#D4B483]/20 flex items-center justify-center">
            <User className="w-7 h-7 text-[#D4B483]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#EDEDED]">{profile.username}</h2>
            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full mt-1 ${
              profile.role === "admin"
                ? "bg-[#D4B483]/15 text-[#D4B483]"
                : "bg-[#3B82F6]/15 text-[#3B82F6]"
            }`}>
              {profile.role === "admin" ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
              {profile.role === "admin" ? "Administratör" : "Personal"}
            </span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <User className="w-4 h-4 text-[#A8ABB2]" />
            <span className="text-[#D4D4D8]">Användarnamn:</span>
            <span className="text-[#EDEDED]">{profile.username}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Calendar className="w-4 h-4 text-[#A8ABB2]" />
            <span className="text-[#D4D4D8]">Skapad:</span>
            <span className="text-[#EDEDED]">{formatDate(profile.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-[#121212] rounded-xl border border-[#27272A] p-6">
        <div className="flex items-center gap-2 mb-5">
          <Lock className="w-5 h-5 text-[#D4B483]" />
          <h3 className="text-lg font-semibold text-[#EDEDED]">Ändra lösenord</h3>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1.5 block">Nuvarande lösenord</Label>
            <Input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              placeholder="Ange nuvarande lösenord"
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11"
              required
            />
          </div>
          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1.5 block">Nytt lösenord</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minst 6 tecken"
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11"
              required
            />
          </div>
          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1.5 block">Bekräfta nytt lösenord</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Upprepa nya lösenordet"
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={saving || !oldPassword || !newPassword || !confirmPassword}
            className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] h-11"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <KeyRound className="w-4 h-4 mr-1" />}
            {saving ? "Sparar..." : "Uppdatera lösenord"}
          </Button>
        </form>
      </div>
    </div>
  );
}
